import { FC, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import CtaButton from '@components/quiz/shared/CtaButton';
import { useAccessToken } from '@hooks/useAccessToken';
import { useAuth0User } from '@hooks/useAuth0User';
import { useProfile, USERS_ME_QUERY_KEY } from '@hooks/useProfile';
import { updateProfileFields } from '@api-service/profile';
import type { UpdateProfileFields } from '@api-service/profile';
import { fetchEntryLeagues } from '@api-service/fpl-entries';
import { MAX_FPL_TEAM_ID } from '@lib/constants';

const DIGITS_ONLY = /^\d+$/;

const toPositiveId = (value: string, max = Number.MAX_SAFE_INTEGER) => {
  if (!DIGITS_ONLY.test(value.trim())) return null;
  const parsed = parseInt(value.trim(), 10);
  return parsed > 0 && parsed <= max ? parsed : null;
};

const INPUT_CLASSES =
  'w-full rounded-lg border border-neutral-300 px-4 py-3 font-open-sans text-base text-neutral-900 focus:border-primary-500 focus:outline-none disabled:bg-neutral-100 disabled:text-neutral-500';

const LABEL_CLASSES =
  'mb-1 block font-open-sans text-sm font-bold text-neutral-800';

const ReadOnlyValue: FC<{ label: string; value: number; dataCy: string }> = ({
  label,
  value,
  dataCy
}) => (
  <div>
    <span className={LABEL_CLASSES}>{label}</span>
    <p
      data-cy={dataCy}
      className="rounded-lg border border-neutral-200 bg-neutral-100 px-4 py-3 font-open-sans text-base text-neutral-600"
    >
      {value}
    </p>
  </div>
);

/**
 * The Mini-League Promise capture form (FFH-1076 / FFH-1597). Requires the
 * session /thank-you preserved via its forced token refresh: the profile
 * write goes through the authenticated PUT /users/me. Renders nothing for
 * logged-out visitors so the page still works as a public landing.
 *
 * The league picker mirrors the readiness funnel's flow (FPL ID → league
 * lookup → select) but deliberately does not reuse LeagueSelectBlock, which
 * is coupled to the ReadinessProvider and Sanity block props. When the
 * lookup fails or returns nothing — the norm pre-GW1, when invitational
 * classic leagues don't exist yet — a numeric free-text field for the league
 * ID is shown instead (FFH-1597 scenario 3).
 *
 * Values already on the profile render read-only: the promise is captured
 * once. (That lock is UI-only for now; the API stays permissive pending the
 * wider promise-data design discussion.)
 */
const MiniLeaguePromiseSection: FC = () => {
  const { user, isLoading: userLoading } = useAuth0User();
  const { profile, isLoading: profileLoading } = useProfile();
  const { accessToken } = useAccessToken();
  const queryClient = useQueryClient();

  const [fplIdInput, setFplIdInput] = useState('');
  const [lookupFplId, setLookupFplId] = useState<number | null>(null);
  const [selectedLeagueId, setSelectedLeagueId] = useState('');
  const [manualLeagueInput, setManualLeagueInput] = useState('');

  const savedFplTeamId = profile?.fplTeamId ?? null;
  const savedMiniLeagueId = profile?.miniLeagueId ?? null;

  // A saved FPL ID skips straight to the league lookup.
  useEffect(() => {
    if (savedFplTeamId !== null && savedMiniLeagueId === null) {
      setLookupFplId(savedFplTeamId);
    }
  }, [savedFplTeamId, savedMiniLeagueId]);

  // Discard any previously selected/entered league whenever the FPL ID being
  // looked up changes, so a re-lookup for a different team can never carry
  // over a selection that belonged to the old one.
  useEffect(() => {
    setSelectedLeagueId('');
    setManualLeagueInput('');
  }, [lookupFplId]);

  const leaguesQuery = useQuery({
    queryKey: ['fpl-entry-leagues', lookupFplId],
    queryFn: () => fetchEntryLeagues(lookupFplId as number),
    enabled: lookupFplId !== null && savedMiniLeagueId === null,
    retry: false,
    refetchOnWindowFocus: false
  });

  const mutation = useMutation({
    mutationFn: async (fields: UpdateProfileFields) => {
      // accessToken is fetched independently of profile (separate
      // useAccessToken() call) and can still be null here; canSubmit gates
      // the button on it, but guard again so a stale click never sends an
      // unauthenticated PUT.
      if (!accessToken) throw new Error('No access token available');

      const result = await updateProfileFields(accessToken, fields);
      if (!result.ok) throw new Error(result.message);
      return result.profile;
    },
    onSuccess: () => {
      // useProfile keys on [USERS_ME_QUERY_KEY, accessToken]; invalidating by
      // the prefix (rather than setQueryData on this component's own
      // accessToken) refreshes whichever token-scoped entry useProfile is
      // actually reading, mirroring useImportFromFpl.
      queryClient.invalidateQueries({ queryKey: USERS_ME_QUERY_KEY });
    }
  });

  if (userLoading || (user && profileLoading)) {
    return (
      <section className="mt-10 h-40 animate-pulse rounded-2xl border border-neutral-200 bg-white" />
    );
  }

  // Logged-out direct visits: the capture needs an authenticated PUT, so the
  // section simply steps aside and the rest of the page carries on.
  if (!user || !profile) return null;

  const showLeagueFallback =
    leaguesQuery.isError ||
    (leaguesQuery.isSuccess && leaguesQuery.data.length === 0);
  const leagues = leaguesQuery.data ?? [];

  const effectiveFplTeamId =
    savedFplTeamId ?? toPositiveId(fplIdInput, MAX_FPL_TEAM_ID);
  const chosenMiniLeagueId = showLeagueFallback
    ? toPositiveId(manualLeagueInput)
    : toPositiveId(selectedLeagueId);

  const isComplete = savedFplTeamId !== null && savedMiniLeagueId !== null;
  // True once the FPL ID that was actually looked up (whose leagues/fallback
  // are on screen) still matches the current input. False the moment the
  // input drifts away from it (edited after a lookup, before re-searching) —
  // without this, submit could pair the new FPL ID with a league selected
  // for the old one.
  const isLookupCurrentForInput =
    lookupFplId !== null && lookupFplId === effectiveFplTeamId;
  const canLookUpLeagues =
    effectiveFplTeamId !== null && lookupFplId !== effectiveFplTeamId;
  const canSubmit =
    effectiveFplTeamId !== null &&
    chosenMiniLeagueId !== null &&
    isLookupCurrentForInput &&
    !!accessToken &&
    !mutation.isLoading;

  const handleSubmit = () => {
    // canSubmit already gates the button on these; re-checked here so a
    // stale click can never pair a drifted FPL ID with an old league choice.
    if (
      effectiveFplTeamId === null ||
      chosenMiniLeagueId === null ||
      !isLookupCurrentForInput
    ) {
      return;
    }

    // The PUT's fplTeamId is full-replace, so the saved value is echoed back
    // alongside the new mini-league; miniLeagueId is only ever set here when
    // none is stored (the form locks once captured).
    mutation.mutate({
      fplTeamId: effectiveFplTeamId,
      miniLeagueId: chosenMiniLeagueId
    });
  };

  return (
    <section className="mt-10 rounded-2xl border border-neutral-200 bg-white px-6 py-8">
      <h2 className="text-center font-sans text-xl font-extrabold text-neutral-900">
        {isComplete
          ? 'Mini-League Promise activated'
          : 'Lock in your mini-league'}
      </h2>
      <p className="mt-2 text-center font-open-sans text-sm text-neutral-600">
        {isComplete
          ? "You're all set — we've registered your mini-league."
          : 'Tell us the mini-league you want to win and we’ll hold you to it.'}
      </p>

      <div className="mt-6 flex flex-col gap-4">
        {savedFplTeamId !== null ? (
          <ReadOnlyValue
            label="Your FPL ID"
            value={savedFplTeamId}
            dataCy="mini-league-promise-fpl-id"
          />
        ) : (
          <div>
            <label htmlFor="promise-fpl-id" className={LABEL_CLASSES}>
              Your FPL ID
            </label>
            <input
              id="promise-fpl-id"
              data-cy="mini-league-promise-fpl-id"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="e.g. 1234567"
              className={INPUT_CLASSES}
              value={fplIdInput}
              onChange={(event) =>
                setFplIdInput(event.target.value.replace(/\D/g, ''))
              }
            />
          </div>
        )}

        {isComplete ? (
          <ReadOnlyValue
            label="Your mini-league ID"
            value={savedMiniLeagueId as number}
            dataCy="mini-league-promise-league-id"
          />
        ) : (
          <>
            {savedFplTeamId === null && (
              <CtaButton
                variant="secondary"
                onClick={() =>
                  effectiveFplTeamId !== null &&
                  setLookupFplId(effectiveFplTeamId)
                }
                disabled={!canLookUpLeagues}
                dataCy="mini-league-promise-find-leagues"
              >
                Find my mini-leagues
              </CtaButton>
            )}

            {leaguesQuery.isFetching && (
              <p className="animate-pulse text-center font-open-sans text-sm text-neutral-600">
                Looking up your mini-leagues…
              </p>
            )}

            {!leaguesQuery.isFetching && leagues.length > 0 && (
              <div>
                <label
                  htmlFor="promise-league-select"
                  className={LABEL_CLASSES}
                >
                  Choose your mini-league
                </label>
                <select
                  id="promise-league-select"
                  data-cy="mini-league-promise-league-select"
                  className={INPUT_CLASSES}
                  value={selectedLeagueId}
                  onChange={(event) => setSelectedLeagueId(event.target.value)}
                >
                  <option value="" disabled>
                    Select a mini-league
                  </option>
                  {leagues.map((league) => (
                    <option key={league.leagueId} value={league.leagueId}>
                      {`${league.name} (${league.leagueId})`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!leaguesQuery.isFetching && showLeagueFallback && (
              <div>
                <label htmlFor="promise-league-id" className={LABEL_CLASSES}>
                  Your mini-league ID
                </label>
                <input
                  id="promise-league-id"
                  data-cy="mini-league-promise-league-id"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="e.g. 987654"
                  className={INPUT_CLASSES}
                  value={manualLeagueInput}
                  onChange={(event) =>
                    setManualLeagueInput(event.target.value.replace(/\D/g, ''))
                  }
                />
                <p className="mt-1 font-open-sans text-xs text-neutral-500">
                  We couldn’t list your leagues yet. Paste the ID from your
                  league’s URL on the FPL site instead.
                </p>
              </div>
            )}

            {!isLookupCurrentForInput &&
              lookupFplId !== null &&
              effectiveFplTeamId !== null && (
                <p className="text-center font-open-sans text-sm text-amber-600">
                  You changed your FPL ID — search again to update your
                  mini-leagues.
                </p>
              )}

            {(leagues.length > 0 || showLeagueFallback) && (
              <CtaButton
                onClick={handleSubmit}
                disabled={!canSubmit}
                dataCy="mini-league-promise-submit"
              >
                {mutation.isLoading ? 'Saving…' : 'Activate my promise'}
              </CtaButton>
            )}

            {mutation.isError && (
              <p className="text-center font-open-sans text-sm text-red-600">
                Something went wrong saving your mini-league. Please try again.
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default MiniLeaguePromiseSection;
